
import { useState, useEffect, useCallback, useRef } from "react";
import { toast as sonnerToast } from "sonner";

export type ToastProps = {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  open: boolean;
  variant?: "default" | "destructive";
};

const generateId = () => Math.random().toString(36).substring(2, 9);

export const useToast = () => {
  const [toasts, setToasts] = useState<ToastProps[]>([]);
  const timeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const removeToast = useCallback((id: string) => {
    setToasts((prevToasts) =>
      prevToasts.map((toast) =>
        toast.id === id ? { ...toast, open: false } : toast
      )
    );

    setTimeout(() => {
      setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
    }, 300);
  }, []);

  useEffect(() => {
    return () => {
      Object.values(timeoutsRef.current).forEach((timeout) => {
        clearTimeout(timeout);
      });
      timeoutsRef.current = {};
    };
  }, []);

  const toast = useCallback(
    ({
      title,
      description,
      action,
      variant = "default",
      duration = 3000,
    }: {
      title?: React.ReactNode;
      description?: React.ReactNode;
      action?: React.ReactNode;
      variant?: "default" | "destructive";
      duration?: number;
    }) => {
      const id = generateId();

      setToasts((prevToasts) => [
        ...prevToasts,
        { id, title, description, action, variant, open: true },
      ]);

      if (duration) {
        timeoutsRef.current[id] = setTimeout(() => {
          removeToast(id);
        }, duration);
      }

      return id;
    },
    [removeToast]
  );

  return {
    toast,
    toasts,
    removeToast,
  };
};

// For compatibility with React-Hot-Toast style imports
export const toast = {
  success: (message: string) => {
    sonnerToast.success(message);
  },
  error: (message: string) => {
    sonnerToast.error(message);
  },
  info: (message: string) => {
    sonnerToast.info(message);
  },
  warning: (message: string) => {
    sonnerToast.warning(message);
  },
  custom: (component: React.ReactNode) => {
    sonnerToast(component);
  }
};
